import { Wrapper, BookingStepButtons, DifferentMessages } from '@/components/VoucherSelector.styles'
import { ProductDetails, SelectedOption, SelectedSessionWithVariant, SelectedVariants, Voucher } from '@/types'
import BookingStep from '@/components/BookingStep'
import React, { useMemo, useState } from 'react'
import { BookingSteps } from '@/components/BookingNew.styles'
import GiftVoucherAmountSelector from '@/components/GiftVoucherAmountSelector'
import VoucherSelectorItem from '@/components/VoucherSelectorItem'
import BookingFinalise from '@/components/BookingFinalise'
import VoucherPostageForm from '@/components/VoucherPostageForm'
import VoucherEmailForm from '@/components/VoucherEmailForm'
import VoucherFieldsForm from '@/components/VoucherFieldsForm'
import OptionSelector from '@/components/OptionSelector'
import { getTotal } from '@/helpers/BookingHelper'
import { Spacing, Button, Body } from '@big-red-group/storefront-common-ui'
import RadioButtonGroup from '@/components/RadioButtonGroup'
import {
  createEVAddToCartRequest,
  createGVAddToCartRequest,
  formatPrice,
  GiftVoucher,
  useAddToCart,
  useCart,
  VoucherDeliveryType,
  VoucherEmailOptions,
  VoucherField,
  IndividualField,
  VoucherPostageOptions,
  reduceFormFields,
  centsToDollars,
  ProductSession,
} from '@big-red-group/storefront-common-checkout'
import { ClickEventHandlerInput } from '@big-red-group/storefront-common-ui/dist/types'
import { useRouter } from 'next/router'
import { useDefaultProperties } from '@/hooks/DefaultPropertiesHook'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import { defaultVoucherDeliveryMethods, defaultVoucherFields } from '@/constant/voucher'
import { useNotification } from '@/hooks/NotificationHook'
import ToggleSwitch from './ToggleSwitch'
import IndividualMessage from './IndividualMessage'
import { scrollToElement } from '@/helpers/PageScrollHelper'

type VoucherSelectorProps =
  | {
      type: 'experience'
      evSessions: ProductSession[]
      giftVoucher?: GiftVoucher
      theme?: never
      onAddToCart: (isGift: boolean, selectedVariants?: SelectedSessionWithVariant[], voucher?: Voucher) => void
      onBookNowClick?: (params: ClickEventHandlerInput) => void
    }
  | {
      type: 'gift'
      evSessions?: never
      giftVoucher: GiftVoucher
      theme: string
      onAddToCart: (isGift: boolean, voucher?: Voucher) => void
      onBookNowClick?: (params: ClickEventHandlerInput) => void
    }

export type ExpandedOption = {
  id: number
  name: string
}

const VoucherSelector: React.FC<VoucherSelectorProps> = ({
  type,
  giftVoucher,
  evSessions,
  theme,
  onAddToCart,
  onBookNowClick,
}) => {
  const defaultProperties = useDefaultProperties()
  const agentHandlerKey = defaultProperties?.agentConfiguration?.handlerKey
  const baseUrl = getUrlPrefix(agentHandlerKey)
  const deliveryMethods = giftVoucher?.deliveryMethods ?? defaultVoucherDeliveryMethods
  const displayAfterPay = defaultProperties?.agentConfiguration?.paymentOptions?.some((po) => po.type === 'AFTERPAY')!!

  const [currentStep, setCurrentStep] = useState(0)
  const [visibleSteps, setVisibleSteps] = useState<number[]>([0])
  const [selectedAmount, setSelectedAmount] = useState<number | undefined>(giftVoucher?.predefinedAmounts?.[0])
  const [hasConfirmedAmount, setHasConfirmedAmount] = useState<boolean>(false)
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[] | undefined>(undefined)
  const [expandedSelectedOptions, setExpandedSelectedOptions] = useState<ExpandedOption[] | undefined>(undefined)
  const [selectedVariants, setSelectedVariants] = useState<SelectedVariants>({})
  const [hasConfirmedOptions, setHasConfirmedOptions] = useState<boolean>(false)
  const [fields, setFields] = useState<VoucherField[]>([])
  const [individualFields, setIndividualFields] = useState<IndividualField[]>([])
  const [wantsIndividualMessages, setWantsIndividualMessages] = useState<boolean>(false)
  const [individualMsgCounter, setIndividualMsgCounter] = useState<number>(0)
  const [hasConfirmedFields, setHasConfirmedFields] = useState<boolean>(false)
  const [deliveryType, setDeliveryType] = useState<VoucherDeliveryType>('ELECTRONIC')
  const [postageOptions, setPostageOptions] = useState<VoucherPostageOptions | null>(null)
  const [hasCompletedPostageOptions, setHasCompletedPostageOptions] = useState<boolean>(false)
  const [emailOptions, setEmailOptions] = useState<VoucherEmailOptions | null>(null)
  const [hasCompletedEmailOptions, setHasCompletedEmailOptions] = useState<boolean>(false)
  const [postageAmount, setPostageAmount] = useState<number>(0)
  const [deliveryMethod, setDeliveryMethod] = useState<string>('')
  const [loadingBooking, setLoadingBooking] = useState<boolean>(false)
  const [selectedQty, setSelectedQty] = useState<number>(1)
  const [isCustomAmountAdded, setIsCustomAmountAdded] = useState<boolean>(false)

  const router = useRouter()
  const addToCart = useAddToCart(baseUrl)
  const { mutate } = useCart(baseUrl)
  const notification = useNotification()

  const getSelectedVariants = (): SelectedVariants => selectedVariants

  const amountSummary: string = useMemo(
    () => (selectedAmount ? `${selectedQty} x ${formatPrice(selectedAmount)}` : ''),
    [selectedAmount, selectedQty]
  )

  const calculateSubTotal = () => {
    const isExperience = type === 'experience'
    const isGift = type === 'gift'

    const optionsTotal = isExperience && selectedOptions?.length ? selectedOptions.reduce(getTotal, 0) : 0
    const giftAmount = isGift && selectedAmount ? centsToDollars(selectedAmount * selectedQty) : 0
    const postage = postageAmount > 0 ? centsToDollars(postageAmount) : 0

    return optionsTotal + giftAmount + postage
  }

  const subTotal = useMemo(
    () => calculateSubTotal(),
    [selectedAmount, selectedOptions, postageAmount, type, selectedQty]
  )

  const isAddToCartButtonDisabled: boolean = useMemo(() => {
    if (
      (type == 'experience' && !hasConfirmedOptions) ||
      (type == 'gift' && !hasConfirmedAmount) ||
      !hasConfirmedFields
    ) {
      return true
    }
    if (deliveryType === 'ELECTRONIC') {
      return !hasCompletedEmailOptions
    }
    if (deliveryType === 'POST') {
      return !hasCompletedPostageOptions
    }
    return true
  }, [hasCompletedEmailOptions, hasCompletedPostageOptions, hasConfirmedAmount, hasConfirmedFields])

  const travellersSummary: string = useMemo(
    () => selectedOptions?.map((option) => `${option.quantity} x ${option.name}`).join(', ') || '',
    [selectedOptions]
  )
  const isMore = (type === 'experience' && expandedSelectedOptions?.length! > 1) || (type === 'gift' && selectedQty > 1)

  const formatMessageSummary = () => {
    if (!wantsIndividualMessages || !isMore) {
      const reducedFields = reduceFormFields(fields)
      return formattedMessageLine(reducedFields['TO'], reducedFields['MESSAGE'], reducedFields['FROM'])
    }

    let messageSummary: string = ''
    individualFields.map(({ formFields }) => {
      const reducedIndividualField = reduceFormFields(formFields)
      messageSummary += formattedMessageLine(
        reducedIndividualField['TO'],
        reducedIndividualField['MESSAGE'],
        reducedIndividualField['FROM']
      )
    })
    return messageSummary
  }

  const formattedMessageLine = (to: string, message: string, from: string) => {
    return `<div class="messages">
              <div><span>To:</span>${to}</div>
              <div><span>From:</span>${from}</div>
              <div><span>Message:</span>${message}</div>
            </div>`
  }

  const messageSummary: any = useMemo(() => (hasConfirmedFields ? formatMessageSummary() : []), [hasConfirmedFields])

  const onSelectedAmountChange = (selectedAmount: number) => {
    setSelectedAmount(selectedAmount)
  }

  const onHasConfirmedExp = () => {
    setHasConfirmedOptions(true)
    if (!visibleSteps.includes(1)) {
      setVisibleSteps([...visibleSteps, 1])
    }
    // Waiting till option selector section closes
    setTimeout(() => {
      scrollToElement('voucher-step-1')
    }, 100)
  }

  const onHasConfirmedAmount = () => {
    setHasConfirmedAmount(true)
    incrementCurrentStep()
    if (!visibleSteps.includes(1)) {
      setVisibleSteps([...visibleSteps, 1])
    }

    setTimeout(() => {
      scrollToElement('voucher-step-1')
    }, 300)
  }

  const setHasCompletedFieldsForm = () => {
    setHasConfirmedFields(true)
    if (!visibleSteps.includes(2)) {
      setVisibleSteps([...visibleSteps, 2])
    }

    setTimeout(() => {
      scrollToElement('voucher-step-2')
    }, 300)
  }

  const compileIndividualFields = (optionId: string, optionIndex: number, fields: VoucherField[]) => {
    const existedIndex = (individualFields || []).findIndex((a) => a.optionIndex === optionIndex)
    if (existedIndex >= 0) {
      individualFields[existedIndex].formFields = fields
      setIndividualFields([...individualFields])
    } else {
      setIndividualFields([...individualFields, { id: optionId, optionIndex: optionIndex, formFields: fields }])
    }
    setIndividualMessageComplete(optionIndex)
  }

  const setIndividualMessageComplete = (index: number) => {
    const optionsLength = type == 'experience' ? expandedSelectedOptions?.length! : selectedQty
    if (index === optionsLength - 1) {
      setHasCompletedFieldsForm()
      return
    }
    setIndividualMsgCounter(index + 1)
  }

  const resetFieldsSection = () => {
    setHasConfirmedFields(false)
    setIndividualMsgCounter(0)
  }

  const incrementCurrentStep = () => {
    const nextStep = currentStep + 1
    setCurrentStep(nextStep)
  }

  const handleOptionQuantityChange = (
    selectedVariants: SelectedVariants,
    selectedOptions: SelectedOption[] | undefined
  ) => {
    setSelectedVariants(selectedVariants)
    setSelectedOptions(selectedOptions)
    expandSelectedOptions(selectedOptions)
    resetFieldsSection()

    const notCompletedIndex = (individualFields || []).findIndex((a) => (a.formFields || []).length === 0)
    setIndividualMsgCounter(notCompletedIndex >= 0 ? notCompletedIndex : 0)
  }

  const expandSelectedOptions = (selectedOptions: SelectedOption[] | undefined) => {
    if (!selectedOptions) {
      setExpandedSelectedOptions(undefined)
      return
    }

    let expandedOptions: ExpandedOption[] = []
    selectedOptions.forEach((option) => {
      expandedOptions.push(
        ...new Array(option.quantity).fill({
          id: option.id,
          name: option.name,
        })
      )
    })
    setExpandedSelectedOptions(expandedOptions)
  }

  const handlePostageOptionChange = (value: string) => {
    const method = deliveryMethods.find((method) => method.key === value)
    setDeliveryMethod(value)
    if (method) {
      setPostageAmount(method?.amount!)
    }
  }

  const handleQtyChange = (value: string) => {
    setSelectedQty(parseInt(value))
  }

  // TODO: Type of event should be a mouse event, but generic type is used in Button component
  const handleAddToCart = async (event: any, buttonText?: string) => {
    setLoadingBooking(true)
    const voucherDetails =
      type === 'gift'
        ? createGVAddToCartRequest(
            selectedQty,
            deliveryType,
            deliveryType === 'ELECTRONIC' ? 'ELECTRONIC' : deliveryMethod,
            selectedAmount!,
            emailOptions,
            postageOptions,
            theme!,
            fields,
            individualFields,
            wantsIndividualMessages
          )
        : createEVAddToCartRequest(
            selectedOptions!,
            deliveryType,
            deliveryType === 'ELECTRONIC' ? 'ELECTRONIC' : deliveryMethod,
            emailOptions,
            postageOptions,
            fields,
            individualFields,
            wantsIndividualMessages
          )

    try {
      for (const detail of voucherDetails) {
        await addToCart({ item: detail })
      }

      await mutate()

      const voucherData: Voucher = {
        type,
        deliveryType: deliveryType,
        isPersonalisedMessages: wantsIndividualMessages,
        giftVoucherCustomAmountAdded: isCustomAmountAdded,
        giftVoucherAmount: selectedAmount,
        giftVoucherQuantity: selectedQty,
      }

      if (type === 'experience') {
        onAddToCart(true, Object.values(selectedVariants!), voucherData)
        onBookNowClick!({
          url: window.location.href,
          location: 'PDP | Experience Gift Voucher',
          text: buttonText,
          event,
        })
      } else {
        onAddToCart(true, voucherData)
        onBookNowClick!({ url: window.location.href, location: 'Gift Vouchers | Gift', text: buttonText, event })
      }

      return router.push(`${baseUrl}/en/order/checkout`)
    } catch (error) {
      return notification('Unable to add items to cart. Please try again later.', 'ERROR', 'error')
    } finally {
      setLoadingBooking(false)
    }
  }

  return (
    <Wrapper>
      <BookingSteps>
        {type === 'gift' && (
          <section id="voucher-step-0">
            <BookingStep
              title="1. Select amount"
              index={0}
              currentStep={currentStep}
              updateCurrentStep={setCurrentStep}
              isDisabled={false}
              onReset={() => setHasConfirmedAmount(false)}
              summary={amountSummary}
              collapsed={hasConfirmedAmount}
              skeleton={!visibleSteps.includes(0)}
            >
              <GiftVoucherAmountSelector
                selectedAmount={selectedAmount}
                setSelectedAmount={onSelectedAmountChange}
                maxAllowedAmount={giftVoucher?.maxAllowedAmount ?? 1000}
                amounts={giftVoucher?.predefinedAmounts ?? []}
                allowCustomAmount={giftVoucher?.allowsCustomAmount ?? false}
                onConfirmAmount={onHasConfirmedAmount}
                selectedQty={selectedQty}
                onQtyChange={handleQtyChange}
                setIsCustomAmountAdded={setIsCustomAmountAdded}
              />
            </BookingStep>
          </section>
        )}
        {type === 'experience' && (
          <section id="voucher-step-0">
            <BookingStep
              title="1. Select recipients"
              index={0}
              currentStep={currentStep}
              updateCurrentStep={setCurrentStep}
              isDisabled={false}
              onReset={() => setHasConfirmedOptions(false)}
              summary={travellersSummary}
              collapsed={hasConfirmedOptions}
              skeleton={!visibleSteps.includes(0)}
            >
              {evSessions ? (
                <OptionSelector
                  isExperienceVoucher
                  previouslySelectedValues={selectedOptions}
                  previouslySelectedSession={undefined}
                  sessions={evSessions}
                  onQuantityChange={(
                    selectedVariants: SelectedVariants,
                    selectedOptions: SelectedOption[] | undefined
                  ) => {
                    handleOptionQuantityChange(selectedVariants, selectedOptions)
                  }}
                  getSelectedVariants={getSelectedVariants}
                  showPricing={true}
                  preselectOption
                />
              ) : (
                <Body size="M" weight="regular">
                  There are no gifting options for this product.
                </Body>
              )}
              <Spacing />
              <BookingStepButtons>
                <Button
                  size="S"
                  onClick={onHasConfirmedExp}
                  dataTestId="voucher-confirm-fields"
                  disabled={!selectedOptions?.length}
                >
                  Confirm
                </Button>
              </BookingStepButtons>
            </BookingStep>
          </section>
        )}
        <section id="voucher-step-1">
          <BookingStep
            title="2. Gift message"
            index={1}
            currentStep={currentStep}
            updateCurrentStep={setCurrentStep}
            isDisabled={false}
            onReset={resetFieldsSection}
            summary={messageSummary}
            collapsed={hasConfirmedFields}
            skeleton={!visibleSteps.includes(1)}
          >
            <Spacing size="XS" />
            {isMore && (
              <>
                <DifferentMessages>
                  <Body weight="medium">Add different personalised messages</Body>
                  <ToggleSwitch
                    handleOnChange={(v) => {
                      setWantsIndividualMessages(v)
                      setIndividualFields([])
                    }}
                    isChecked={wantsIndividualMessages}
                    id="individualMessages"
                  />
                </DifferentMessages>
                <Spacing size="S" />
              </>
            )}
            {wantsIndividualMessages && isMore ? (
              <IndividualMessage
                individualMsgCounter={individualMsgCounter}
                setIndividualMsgCounter={setIndividualMsgCounter}
                allOptions={expandedSelectedOptions}
                giftQty={selectedQty}
                individualFields={individualFields}
                setIndividualFields={setIndividualFields}
                setFields={() => {}}
                compileIndividualFields={compileIndividualFields}
                giftVoucherFields={giftVoucher?.fields || defaultVoucherFields}
                type={type}
              />
            ) : (
              <VoucherFieldsForm
                setVoucherFields={setFields}
                compileIndividualFields={() => {}}
                setHasCompletedFieldsForm={setHasCompletedFieldsForm}
                fields={(giftVoucher?.fields || defaultVoucherFields).map((d) => ({
                  ...d,
                  value: fields?.find((f) => f[d.key])?.[d.key] || d.value,
                }))}
              />
            )}
          </BookingStep>
        </section>
        {/* TODO: uncomment when postage option is available */}
        {/*<section id="voucher-step-2">*/}
        {/*  <VoucherSelectorItem heading="How should we send your voucher?">*/}
        {/*    <DeliveryMethodWrapper>*/}
        {/*      <FilterButton*/}
        {/*        label="Send via Email"*/}
        {/*        icon="email"*/}
        {/*        selected={deliveryType === 'ELECTRONIC'}*/}
        {/*        onClick={() => setDeliveryType('ELECTRONIC')}*/}
        {/*        dataTestId="voucher-delivery-email"*/}
        {/*      />*/}
        {/*      <FilterButton*/}
        {/*        label="Send via Post"*/}
        {/*        icon="paperPlane"*/}
        {/*        selected={deliveryType === 'POST'}*/}
        {/*        onClick={() => setDeliveryType('POST')}*/}
        {/*        dataTestId="voucher-delivery-post"*/}
        {/*      />*/}
        {/*    </DeliveryMethodWrapper>*/}
        {/*  </VoucherSelectorItem>*/}
        {/*</section>*/}
        <section id="voucher-step-2">
          {deliveryType === 'ELECTRONIC' && (
            <VoucherSelectorItem heading="3. Send to" skeleton={!visibleSteps.includes(2)}>
              <VoucherEmailForm
                setEmailOptions={setEmailOptions}
                setHasCompletedEmailForm={setHasCompletedEmailOptions}
              />
            </VoucherSelectorItem>
          )}
          {deliveryType === 'POST' && (
            <>
              <VoucherSelectorItem heading="3. Delivery method" skeleton={!visibleSteps.includes(2)}>
                <RadioButtonGroup
                  options={deliveryMethods
                    .filter((method) => method.requiresPhysicalAddress)
                    .map((method) => ({
                      label: method.label,
                      value: method.key,
                      description: formatPrice(method.amount),
                    }))}
                  onChange={(value) => {
                    handlePostageOptionChange(value)
                  }}
                  name="optional-pickup"
                  showDescription
                />
              </VoucherSelectorItem>

              <VoucherSelectorItem heading="4. Send to" skeleton={!visibleSteps.includes(2)}>
                <VoucherPostageForm
                  setPostageOptions={setPostageOptions}
                  setHasCompletedPostForm={setHasCompletedPostageOptions}
                />
              </VoucherSelectorItem>
            </>
          )}
        </section>
      </BookingSteps>
      {subTotal > 0 && (
        <BookingFinalise
          loadingBooking={loadingBooking}
          handleBook={handleAddToCart}
          subtotal={subTotal}
          disabled={isAddToCartButtonDisabled}
          displayAfterpay={displayAfterPay}
        />
      )}
    </Wrapper>
  )
}

export default VoucherSelector
